import {
  BaseModel,
  useInScopeModel,
} from '@cotera/client/v0/actions/framework';
import {
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import type { QueryClient } from '@tanstack/react-query';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { MultiKeyUrlParamWatchable } from '@cotera/client/app/etc/url-param-watchable';
import {
  RESOURCE_LIST_FETCH_STALE_MS,
  queryKeys,
} from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import { WatchablesFactory } from '../watchables/watchables-factory';
import type { DatasetWireType } from './dataset-utils';

export type DatasetListItem = {
  id: string;
  ownerId: string;
  type: DatasetWireType;
  name: string;
  originalFilename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  folderName: string | null;
  folderScope: 'user' | 'org' | 'external' | null;
  scope: 'user' | 'org' | 'external' | null;
  updatedAt: Date;
};

export type ListParamsState = {
  page: number;
  limit: number;
  search: string;
  sort: string | undefined;
  sortDir: 'asc' | 'desc';
};

export const DEFAULT_LIST_PARAMS: ListParamsState = {
  page: 1,
  limit: 100,
  search: '',
  sort: undefined,
  sortDir: 'asc',
};

function parsePage(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_LIST_PARAMS.page;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 1
    ? Math.floor(n)
    : DEFAULT_LIST_PARAMS.page;
}

function parseLimit(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_LIST_PARAMS.limit;
  }
  const n = Number(s);
  return Number.isFinite(n)
    ? Math.min(100, Math.max(1, Math.floor(n)))
    : DEFAULT_LIST_PARAMS.limit;
}

function parseListParams(params: URLSearchParams): ListParamsState {
  return {
    page: parsePage(params.get('page') ?? ''),
    limit: parseLimit(params.get('limit') ?? ''),
    search: params.get('search') ?? DEFAULT_LIST_PARAMS.search,
    sort: (() => {
      const v = params.get('sort');
      return v !== null && v !== '' ? v : undefined;
    })(),
    sortDir:
      params.get('sortDir') === 'desc' ? 'desc' : DEFAULT_LIST_PARAMS.sortDir,
  };
}

function serializeListParams(s: ListParamsState): Record<string, string> {
  return {
    page: s.page === DEFAULT_LIST_PARAMS.page ? '' : String(s.page),
    limit: String(s.limit),
    search: s.search,
    sort: s.sort ?? '',
    sortDir: s.sortDir === 'asc' ? '' : s.sortDir,
  };
}

function listParamsEquality(a: ListParamsState, b: ListParamsState): boolean {
  return (
    a.page === b.page &&
    a.limit === b.limit &&
    a.search === b.search &&
    a.sort === b.sort &&
    a.sortDir === b.sortDir
  );
}

export type DatasetsListData = {
  items: DatasetListItem[];
  total: number;
};

export type DatasetsListPageModelProps = {
  orgId: string;
  queryClient: QueryClient;
  watchablesFactory: WatchablesFactory;
  urlAdapter: UrlParamAdapter;
  initialSearchParams: URLSearchParams;
  client: TenantedClient;
};

function datasetsDataEquality(
  a: DatasetsListData | null,
  b: DatasetsListData | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (a.total !== b.total || a.items.length !== b.items.length) {
    return false;
  }
  for (let i = 0; i < a.items.length; i++) {
    if (
      a.items[i]?.id !== b.items[i]?.id ||
      a.items[i]?.name !== b.items[i]?.name ||
      a.items[i]?.originalFilename !== b.items[i]?.originalFilename ||
      a.items[i]?.folderName !== b.items[i]?.folderName ||
      a.items[i]?.folderScope !== b.items[i]?.folderScope ||
      a.items[i]?.scope !== b.items[i]?.scope ||
      a.items[i]?.updatedAt?.getTime() !== b.items[i]?.updatedAt?.getTime()
    ) {
      return false;
    }
  }
  return true;
}

export class DatasetsListPageModel extends BaseModel {
  private readonly _listParamsUrl: MultiKeyUrlParamWatchable<ListParamsState>;
  private readonly _listParams: ReadonlyWatchable<ListParamsState>;
  private readonly _datasetsDataEventWatchable: ReadonlyEventWatchable<DatasetsListData | null>;

  constructor(props: DatasetsListPageModelProps) {
    super('datasets-list-page');
    const initial = parseListParams(props.initialSearchParams);
    const adapter = props.urlAdapter;

    this._listParamsUrl = new MultiKeyUrlParamWatchable<ListParamsState>(
      ['page', 'limit', 'search', 'sort', 'sortDir'],
      {
        default: DEFAULT_LIST_PARAMS,
        parse: parseListParams,
        serialize: serializeListParams,
        equalityFn: listParamsEquality,
      },
      adapter,
      initial
    );

    this._listParams = this._listParamsUrl.asWatchable();

    this._datasetsDataEventWatchable =
      props.watchablesFactory.makeResourceUpdatedWatchable<
        DatasetsListData | null,
        { resourceType?: string }
      >({
        runOnMount: true,
        initialValue: null,
        equalityFn: datasetsDataEquality,
        filter: (payload) => payload?.resourceType === 'dataset',
        fn: async (_payload, get) => {
          const { page, limit, search, sort, sortDir } = get(this._listParams);
          const allItems: DatasetListItem[] = [];
          let total = 0;
          for (let p = 1; p <= page; p++) {
            const offset = (p - 1) * limit;
            const out = await props.queryClient.fetchQuery({
              queryKey: queryKeys.resource.listPage({
                orgId: props.orgId,
                type: ResourceType.Dataset,
                limit,
                offset,
                ...(search !== undefined && search !== '' && { search }),
                ...(sort !== undefined && sort !== '' && { sort, sortDir }),
              }),
              staleTime: RESOURCE_LIST_FETCH_STALE_MS,
              queryFn: async () =>
                Assert.assertOk(
                  await props.client.resource.list({
                    type: ResourceType.Dataset,
                    limit,
                    offset,
                    ...(search !== undefined && search !== '' && { search }),
                    ...(sort !== undefined && sort !== '' && { sort, sortDir }),
                  })
                ),
            });
            if (out.type !== 'dataset') {
              break;
            }
            total = out.total;
            allItems.push(
              ...out.items.map(
                (dataset): DatasetListItem => ({
                  id: dataset.id,
                  ownerId: dataset.ownerId,
                  type: dataset.type,
                  name: dataset.name,
                  originalFilename: dataset.originalFilename,
                  mimeType: dataset.mimeType,
                  sizeBytes: dataset.sizeBytes,
                  folderName: dataset.folderName ?? null,
                  folderScope: dataset.folderScope ?? null,
                  scope: dataset.scope ?? null,
                  // Constructed, not passed through. The typed client types
                  // this as a `Date`, but the transport is axios' JSON.parse
                  // with no reviver, so what actually arrives is the ISO
                  // string the API declares (`z.string().datetime()`).
                  //
                  // The type being wrong made it look safe to call `getTime()`
                  // on: `datasetsDataEquality` did, threw inside the
                  // watchable's `set`, and took the whole refresh down with it
                  // — so the list silently stopped updating on the first
                  // `resource-updated` event after mount. Building a real Date
                  // here is what makes this model's own `Date` type true.
                  updatedAt: new Date(dataset.updatedAt),
                })
              )
            );
            if (out.items.length < limit) {
              break;
            }
          }
          return { items: allItems, total };
        },
      });
  }

  get listParamsWatchable(): ReadonlyWatchable<ListParamsState> {
    return this._listParams;
  }

  get datasetsDataWatchable(): ReadonlyEventWatchable<DatasetsListData | null> {
    return this._datasetsDataEventWatchable;
  }

  syncFromUrl(searchParams: URLSearchParams): void {
    this._listParamsUrl.syncFromUrl(searchParams);
    void this._datasetsDataEventWatchable.refresh();
  }

  refresh(): void {
    void this._datasetsDataEventWatchable.refresh();
  }

  loadPage(page: number, take: number): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, page, limit: take });
  }

  setSearch(search: string): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, search, page: 1 });
  }

  setSort(sort: string | undefined, sortDir: 'asc' | 'desc'): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, sort, sortDir, page: 1 });
  }
}

export function useDatasetsListPageModel(): DatasetsListPageModel {
  return useInScopeModel(DatasetsListPageModel);
}
